import { Box, Tab, Tabs } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useAppContext from '../../../../../hooks/useAppContext/useAppContext';
import usePractitionerControlsContext from '../../../../../hooks/usePractitionerControlsContext/usePractitionerControlsContext';
import useUploadContext from '../../../../../hooks/useUploadContext/useUploadContext';
import { getParameterByName } from '../../../../ControlPanel/ControlPanel';
import ButtonUI from '../../../../UI/Button/Button';
import { UploadStyle } from './styles';
import UploadedList from './UploadedList/UploadedList';

import { MediaTypes } from '../../../../../commands/practitioner/enums/MediaTypes';
import { UploadPanelTabs } from '../../../../../commands/practitioner/enums/UploadPanelTabs';
import BackendService from '../../../../../services/BackendService';
import { metrics } from '../../../../../metrics/sentryMetrics';
import { a11yProps } from '../../MediaModal';
import { FILE_SERVICE_URL } from './constants';
import AudioUpload from './Upload/AudioUpload/AudioUpload';
import FileUpload from './Upload/FileUpload/FileUpload';
import ImageUpload from './Upload/ImageUpload/ImageUpload';
import VideoUpload from './Upload/VideoUpload/VideoUpload';
interface UploadPanelProps {
  className?: string;
  index: number;
  value: number;
}

export function UploadPanel(props: UploadPanelProps) {
  const { uploadContainer } = UploadStyle;
  const { className, value, index, ...other } = props;
  const token = getParameterByName('token') as string;
  const { t } = useTranslation();
  const [triggerSaveClick, setTriggerSaveClick] = useState<boolean>(false);
  const { sessionId, treatmentId } = useAppContext();
  const {
    setOpenMediaModal,
    practitionerMediaURI,
    isMediaLoaded,
    setPractitionerMediaURI,
    setPractitionerMediaType,
    projectionType,
    setHasBeenSaved,
    practitionerMediaType,
    setStatus,
    setStartResetTimeout,
    uploadUploaded,
    setUploadUploaded,
  } = usePractitionerControlsContext();
  const {
    setIsUploadTabIsOpen,
    setIsLoading,
    uploadedFileName,
    setUploadedFileName,
    setAudioTypeFilename,
    setProgress,
    setFileInput,
    fileInput,
    uploadRequest,
  } = useUploadContext();
  const isSaveDisabled = Boolean(!practitionerMediaURI || !isMediaLoaded);

  useEffect(() => {
    setPractitionerMediaURI('');
    setIsUploadTabIsOpen(true);

    return () => {
      setIsUploadTabIsOpen(false);
    };
  }, []);

  useEffect(() => {
    if (triggerSaveClick) {
      saveClicked();
    }
  }, [triggerSaveClick]);

  useEffect(() => {
    if (!token || fileInput.length === 0) return;

    const uploadFile = async () => {
      setIsLoading(true);

      try {
        const formData = new FormData();
        const file = fileInput[0];

        if (!(file instanceof File)) return;

        formData.append('file', file, 'name');

        const url = `${FILE_SERVICE_URL}/file/upload?treatmentId=${treatmentId}&sessionId=${sessionId}&token=${token}`;

        const startedAt = performance.now();
        let status: 'success' | 'error' = 'success';
        let errorMsg: string | undefined;
        try {
          const data = await uploadFileWithProgress(url, formData, setProgress, uploadRequest);
          const ms = performance.now() - startedAt;
          metrics.distribution('file.upload.duration_ms', ms, {
            status,
            mime: (file as File)?.type,
            ext: (file as File)?.name?.split('.')?.pop(),
            bytes: (file as File)?.size,
            sessionId,
            treatmentId,
          });

          setStatus(true);
          const { url: uploadedUrl, expiresAt } = data;

          if (practitionerMediaType === MediaTypes.AUDIO) {
            setAudioTypeFilename(uploadedFileName);
          } else {
            setAudioTypeFilename('');
          }

          const uploadedFile = {
            url: uploadedUrl,
            uploadedType: practitionerMediaType,
            name: uploadedFileName,
            date: expiresAt,
          };

          const uploadedFiles = localStorage.getItem('uploadedList');
          const prevState = uploadedFiles ? JSON.parse(uploadedFiles) : [];
          localStorage.setItem('uploadedList', JSON.stringify([...prevState, uploadedFile]));

          setPractitionerMediaURI(uploadedUrl);
        } catch (err) {
          status = 'error';
          errorMsg = String(err?.message ?? err);
          const ms = performance.now() - startedAt;
          metrics.distribution('file.upload.duration_ms', ms, {
            status,
            error: errorMsg,
            mime: (file as File)?.type,
            ext: (file as File)?.name?.split('.')?.pop(),
            bytes: (file as File)?.size,
            sessionId,
            treatmentId,
          });
          throw err;
        }
      } catch (err) {
        console.error(err);
        setStatus(false);
      } finally {
        setIsLoading(false);
        setProgress(0);
        setFileInput([]);
        setUploadedFileName('');
      }
    };

    uploadFile();
  }, [fileInput]);

  const uploadFileWithProgress = (
    url: string,
    formData: FormData,
    onProgress: (progress: number) => void,
    uploadRequest: React.MutableRefObject<XMLHttpRequest | null>
  ): Promise<any> => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      uploadRequest.current = xhr;

      xhr.upload.onprogress = event => {
        if (event.lengthComputable) {
          const percent = Math.round((event.loaded / event.total) * 100);
          onProgress(percent);
        }
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const response = JSON.parse(xhr.responseText);
            resolve(response);
          } catch (e) {
            reject(new Error('Invalid JSON response'));
          }
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      };

      xhr.onerror = () => {
        reject(new Error('Network error during upload'));
      };

      xhr.onabort = () => {
        reject(new Error('Upload aborted by user'));
      };

      xhr.open('POST', url);
      xhr.send(formData);
    });
  };

  const handleChange = (event: React.MouseEvent<HTMLElement>, newValue: number) => {
    if (newValue === null) return;
    setPractitionerMediaType(newValue);
    setTriggerSaveClick(false);
    setPractitionerMediaURI('');
  };

  const selectTab = (event: React.SyntheticEvent, tab: number) => {
    setUploadUploaded(tab);
    setTriggerSaveClick(false);
    setPractitionerMediaURI('');
  };

  const upload = (data: any) => {
    setFileInput(prevState => [...prevState, data]);
  };
  const onTrigger = () => {
    setStatus(true);
    setTimeout(() => {
      setTriggerSaveClick(true);
    }, 300);
  };

  const onCloseModal = useCallback(() => {
    setOpenMediaModal(false);
  }, [setOpenMediaModal]);

  const saveClicked = async () => {
    if (isMediaLoaded) {
      // Send loading to the practitioner to update the state
      await BackendService.sendMediaStatusReport(sessionId, treatmentId, practitionerMediaURI, 0, 0);
      await BackendService.sendLoadMedia(
        sessionId,
        treatmentId,
        practitionerMediaURI,
        practitionerMediaType,
        projectionType,
        'Off'
      );
    }
    setAudioTypeFilename('');
    setHasBeenSaved(true);
    setTriggerSaveClick(false);
    setOpenMediaModal(false);
    setStartResetTimeout(true);
  };

  const renderCorrectTypeTab = useMemo(() => {
    switch (practitionerMediaType) {
      case MediaTypes.VIDEO:
        return <VideoUpload index={UploadPanelTabs.Upload} value={uploadUploaded} />;
        break;
      case MediaTypes.IMAGE:
        return <ImageUpload index={UploadPanelTabs.Upload} value={uploadUploaded} />;
        break;
      case MediaTypes.AUDIO:
        return <AudioUpload index={UploadPanelTabs.Upload} value={uploadUploaded} />;
        break;
      default:
        return <VideoUpload index={UploadPanelTabs.Upload} value={uploadUploaded} />;
    }
  }, [practitionerMediaType, uploadUploaded]);

  return (
    <>
      <Box
        sx={uploadContainer}
        role="psylarispanel"
        hidden={value !== index}
        id={`vertical-tabpanel-${index}`}
        aria-labelledby={`vertical-tab-${index}`}
        {...other}
      >
        <Box className={'top-container'}>
          <Tabs className={'tabs-group'} value={uploadUploaded} onChange={selectTab}>
            <Tab label={t('upload')} {...a11yProps(UploadPanelTabs.Upload)} />
            <Tab label={t('uploaded')} {...a11yProps(UploadPanelTabs.Uploaded)} />
          </Tabs>
        </Box>
        <Box className="content-container">
          <FileUpload
            index={UploadPanelTabs.Upload}
            value={uploadUploaded}
            onTypeSelect={handleChange}
            onFileUpload={upload}
          />
          {renderCorrectTypeTab}
          <UploadedList index={UploadPanelTabs.Uploaded} value={uploadUploaded} onSelect={onTrigger} />
        </Box>
        <Box className={'apply-buttons'}>
          <>
            <ButtonUI
              className={'cancel-btn'}
              text={t('media-modal-cancel')}
              onClickHandler={onCloseModal}
              variant={'outlined'}
            />
            <ButtonUI
              className={'save-button'}
              text={t('media-modal-save')}
              onClickHandler={saveClicked}
              disabled={isSaveDisabled}
            />
          </>
        </Box>
      </Box>
    </>
  );
}
